import { logout, useCurrentUser } from '@ctcells/api'
import { dataSourceFor } from '@ctcells/core'
import { t, type MessageKey } from '@ctcells/i18n'

import type { ConsoleHeaderProps } from './ConsoleHeader.tsx'

/**
 * 사이드바 하단 사용자 영역에 들어갈 값 — **실물 세션에서 뽑는다.**
 *
 * 🔴 **갈리는 층은 `dataSourceFor('auth')` 하나다.**
 *    - `demo` → 아무것도 내지 않는다. `ConsoleHeader` 의 기본값(fixture `CURRENT_ACCOUNT`)이 그대로 산다.
 *    - `live` → 로그인 응답의 `TokenResponse.user` 를 그대로 찍는다. **fixture 로 떨어지지 않는다** —
 *      사용자를 모르면 `—` 다. 남의 이름(`eric.cho`)이 뜨는 쪽이 훨씬 나쁘다.
 *
 * ⚠ **현재 사용자 조회 경로가 생겼다** — `GET /auth/me` 가 있다(2026-09-26 확인 · admin·operator·viewer 3역할 · 응답 `{ user_id, role, operations[] }` — `operations` 는 그 역할이 부를 수 있는 operationId 목록).
 *   다만 **이름(`username`·`name`)은 그 응답에 없다** — 이 훅이 찍는 값의 출처는 여전히 로그인 응답
 *   하나뿐이고(갈아탈지는 eric 결정거리), 새로고침 뒤에는 `sessionStorage` 에 저장해 둔 그 값을
 *   `restoreSession()` 이 되얹는다(`tokenStore.ts`). 탭을 닫으면 자격과 함께 사라지고 다시 로그인한다.
 *   저장된 값마저 없으면 **이름이 `—` 로 뜬다** — 모르는 것을 모른다고 표시하는 쪽이다.
 *
 * ⚠ **이메일이 계약에 없다.** `User` 는 `username`(로그인 ID)·`name`(표시 이름)뿐이다.
 *   그래서 계정 메뉴 머리줄에 찍히는 것은 이메일이 아니라 표시 이름이다.
 *
 * 🔴 **자동 로그아웃 잔여 시간은 이 훅이 더는 내지 않는다**(eric 지시 2026-09-21 「저거 시간
 *    그냥 빼자」). 서버가 주는 것은 `Session.expires_at`(**절대 만료**)뿐이고 무활동 잔여
 *    (UAM-SES-06)와 다른 값이라, 실물에서는 늘 `—` 가 찍혀 있었다 → **BR-53**.
 *    되살릴 때도 **절대 만료에서 현재 시각을 빼서 만들지 마라** — 그 순간 한 번 맞을 뿐이고
 *    초 단위로 줄이려면 프론트가 카운트다운을 돌려야 한다(DP-04). 서버가 준 표기를 그대로 찍는다.
 *
 * 🔴 **헤더 줄에는 `name`(표시 이름)을 찍는다**(eric 2026-09-22 「eric.cho 라고 나오는데 계정
 *   같은데, 설정 이름 말이야」). 종전엔 `username`(로그인 ID)이었다 — 와이어프레임의 그 자리가
 *   계정 핸들이라 그렇게 옮겼는데 eric 이 뒤집었다. 로그인 ID 는 계정 메뉴를 열면 머리줄에
 *   나온다(역할과 함께). 표시 이름이 비면 로그인 ID 로 떨어진다. 되돌리지 마라.
 */
export type NavAccount = Pick<ConsoleHeaderProps, 'account' | 'onLogout'>

export function useNavAccount(): NavAccount {
  const user = useCurrentUser()

  // 🔴 로그아웃은 **데이터 소스와 무관하다.** 로그인은 `demo` 에서도 실물 `POST /auth/login` 을
  //    타므로(`LoginRoute`) 세션은 언제나 진짜다 — demo 인 것은 화면에 찍는 값뿐이다.
  const nav: NavAccount = { onLogout: endSessionAndLeave }

  // demo 는 계정 키를 안 내보낸다 — 스프레드해도 키가 없어서 ConsoleHeader 의 기본값(fixture)이 이긴다
  if (dataSourceFor('auth') === 'demo') return nav

  const username = emptyToUndefined(user?.username)
  const displayName = emptyToUndefined(user?.name)

  return {
    ...nav,
    account: {
      // 표시 이름이 비면 로그인 ID 로 떨어진다. 둘 다 없으면 「모름」이다
      name: displayName ?? username ?? t('mask.none'),
      // 메뉴 머리줄 = 로그인 ID(계정을 가르는 값). 표시 이름과 같으면 두 번 안 적는다
      ...(username === undefined || username === displayName ? {} : { detail: username }),
      ...(user === null ? {} : { role: t(`role.${user.role}` as MessageKey) }),
    },
  }
}

/**
 * 로그아웃 — **세션을 비우는 것이 전부다. 화면 이동을 여기서 하지 않는다.**
 *
 * `logout()` 이 `POST /auth/logout` 으로 서버 세션을 폐기하고(UAM-SES-03),
 * **성공·실패와 무관하게** 로컬 정리를 한다(`endSession()`):
 * access 토큰(메모리) · refresh 토큰 · 저장해 둔 `user` · `session_id` 를 지우고,
 * `configureSessionTeardown` 으로 등록된 정리(쿼리 캐시 비우기 — `AppRuntime`)를 부른다.
 *
 * 🔴 **네트워크가 끊겼다고 로그아웃이 안 되면 안 된다.** 서버 호출이 실패해도 자격은 버린다 —
 *    서버 쪽 세션은 만료로 정리된다. 반대로 자격이 남으면 다음 요청이 계속 401 을 맞는다.
 * 🔴 **refresh 토큰을 지우는 것이 핵심이다.** 남겨 두면 새로고침 때 부팅 복구가 다시 로그인시킨다.
 * 🔴 이동은 `RequireAuth` 가 한다 — 세션이 `anonymous` 가 되는 순간 로그인으로 보낸다.
 *    여기서 `navigate` 를 부르면 「세션은 살아 있는데 화면만 로그인」이 가능해진다.
 *
 * ⚠ `useLogout()`(mutation) 을 쓰지 않았다. 그 훅은 `QueryClientProvider` 를 요구하는데
 *   셸이 그것 때문에 마운트 못 하는 상황(테스트·부분 렌더)을 만들 이유가 없다. 캐시 비우기는
 *   `AppRuntime` 이 등록한 teardown 이 이미 한다 — 같은 일을 두 경로로 하지 않는다.
 */
function endSessionAndLeave(): void {
  void logout()
}

function emptyToUndefined(value: string | undefined): string | undefined {
  return value === undefined || value.trim() === '' ? undefined : value
}
